import {Accordion, Button} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Container} from 'src/design/atoms/Container';
import {List} from 'src/design/atoms/List';
import {Paper} from 'src/design/atoms/Paper';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {Title} from 'src/design/atoms/Title';
import {IconAlertCircle} from '@tabler/icons-react';
import {Link, useLocation} from 'react-router-dom';

interface IncompatibleBrowserPageProps {
  browserInfo?: string;
  originalUrl: string;
}

const IncompatibleBrowserPage = ({
  browserInfo,
  originalUrl,
}: IncompatibleBrowserPageProps) => {
  const location = useLocation();
  const stateBrowserInfo = location.state?.browserInfo;
  const displayBrowserInfo = browserInfo || stateBrowserInfo;

  return (
    <Container size={420} my={40}>
      <Stack>
        <Title ta="center">Incompatible Browser</Title>
        <Text c="dimmed" size="sm" ta="center">
          You'll need to use a different browser to access this application.
        </Text>

        <Paper withBorder shadow="md" p={30} radius="md">
          <Stack gap="lg">
            <Text c="dimmed" size="sm">
              Your browser is not compatible with this application. Please use a
              modern browser such as Chrome, Firefox, Safari, or Edge.
            </Text>
            <Text c="dimmed" size="sm">
              <b>This is not an error.</b> You can continue to use the
              application by using a different browser.
            </Text>

            {displayBrowserInfo && (
              <Alert color="gray" icon={<IconAlertCircle />}>
                <Text c="dimmed" size="sm">
                  {displayBrowserInfo}
                </Text>
              </Alert>
            )}

            <Button
              component={Link}
              size="md"
              to={originalUrl}
              target="_system"
              fullWidth>
              Try to open in system browser
            </Button>
          </Stack>
        </Paper>

        <Accordion variant="contained">
          <Accordion.Item value="browser-help">
            <Accordion.Control>
              <Text size="sm">Need help with browser compatibility?</Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="xs">
                <Text c="dimmed" size="xs">
                  This application requires a modern web browser. You may see
                  this message if you're using:
                </Text>
                <List c="dimmed" size="xs" spacing="xs">
                  <List.Item>
                    Internet Explorer (any version) - This browser is no longer
                    supported
                  </List.Item>
                  <List.Item>
                    Old versions of Microsoft Edge (before version 79) - Please
                    update to the latest version
                  </List.Item>
                  <List.Item>
                    Very old versions of Chrome, Firefox, or Safari - Please
                    update to the latest version
                  </List.Item>
                </List>
                <Text c="dimmed" size="xs">
                  <strong>Recommended browsers:</strong> Chrome, Firefox,
                  Safari, or Edge (latest versions)
                </Text>
                <Text c="dimmed" size="xs">
                  If you're using a supported browser and still see this
                  message, try refreshing the page or clearing your browser
                  cache. If you continue to have issues, please contact your
                  organization's administrator.
                </Text>
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      </Stack>
    </Container>
  );
};

export default IncompatibleBrowserPage;
